/**
 * Hook pour gérer les groupes sauvegardés de participants
 * Permet de créer, lire, modifier et supprimer des groupes d'emails
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@core/lib/supabase";
import { useAuth } from "@core/contexts/AuthContext";
import { logger } from "@core/lib/logger";
import { ErrorFactory, logError } from "@core/lib/error-handling";
import { canCreateGroup } from "@core/constants/quotas";

/** Préfixe distinctif (cf. usePolls.ts POLL_LIMIT_REACHED) : les appelants
 * l'interceptent pour ouvrir la modal quota plutôt qu'un toast d'erreur
 * générique. */
export const GROUP_PLAN_LIMIT_PREFIX = "PLAN_LIMIT_REACHED:";

export interface DatePollGroup {
  id: string;
  creator_id: string;
  name: string;
  member_emails: string[];
  created_at: string;
  updated_at: string;
  weekly_digest_enabled?: boolean;
  digest_day?: number; // 1=Lundi, 2=Mardi, ..., 7=Dimanche
  digest_interval?: number; // 1=chaque semaine, 2=toutes les 2 semaines, etc.
  last_digest_sent_at?: string;
  next_digest_due_at?: string;
}

export interface CreateGroupInput {
  name: string;
  member_emails: string[];
}

/**
 * Normalise un groupe issu de la base : `member_emails` peut être `null`
 * côté DB (ligne ancienne, colonne nullable…), alors que tous les
 * consommateurs (GroupsPage, GroupList, GroupSelector, GroupEditor)
 * appellent `.slice()/.map()/.length/.some()` dessus sans garde.
 * Sans cette normalisation, un groupe sans membres fait planter le rendu
 * avec « Cannot read properties of undefined (reading 'slice') » (Sentry
 * #127736069). On garantit donc toujours un tableau.
 */
function normalizeGroup(group: DatePollGroup): DatePollGroup {
  return {
    ...group,
    member_emails: Array.isArray(group.member_emails)
      ? group.member_emails
      : [],
  };
}

export interface UpdateGroupInput {
  name?: string;
  member_emails?: string[];
  weekly_digest_enabled?: boolean;
  digest_day?: number;
  digest_interval?: number;
}

export function useGroups() {
  const { user, profile } = useAuth();
  const [groups, setGroups] = useState<DatePollGroup[]>([]);
  // Groupes CONSERVÉS mais plus utilisables — ceux créés pendant un essai
  // terminé. La politique RLS les retire de `groups` ; sans ce compteur ils
  // disparaîtraient de l'écran sans un mot, ce qui est exactement l'angoisse
  // « j'ai tout perdu » que l'atterrissage en douceur cherche à éviter.
  const [lockedCount, setLockedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Charger tous les groupes de l'utilisateur
  const loadGroups = useCallback(async () => {
    if (!user) {
      setGroups([]);
      setLockedCount(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: supabaseError } = await supabase
        .from("date_poll_groups")
        .select("*")
        .eq("creator_id", user.id)
        .order("updated_at", { ascending: false });

      if (supabaseError) {
        throw ErrorFactory.storage(
          `Failed to load groups: ${supabaseError.message}`,
        );
      }

      setGroups((data || []).map(normalizeGroup));
      logger.info(`Loaded ${data?.length || 0} groups`, "groups");

      // Échec non bloquant : ne pas savoir combien de groupes sont conservés
      // ne doit jamais empêcher d'afficher ceux qui sont utilisables.
      const { data: verrouilles, error: lockedError } = await supabase.rpc(
        "groups_locked_count",
      );
      if (lockedError) {
        logger.warn("Failed to read locked group count", "groups", lockedError);
        setLockedCount(0);
      } else {
        setLockedCount(typeof verrouilles === "number" ? verrouilles : 0);
      }
    } catch (err) {
      const error = err as Error;
      setError(error);
      logError(error, { component: "useGroups", operation: "loadGroups" });
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Charger les groupes au montage et quand l'utilisateur change
  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // Créer un nouveau groupe
  const createGroup = useCallback(
    async (input: CreateGroupInput): Promise<DatePollGroup | null> => {
      if (!user) {
        throw ErrorFactory.auth("User must be authenticated to create groups");
      }
      // Groupes réutilisables = fonctionnalité payante (SOLO+). Gate ici,
      // au plus près de l'écriture, plutôt que côté route seulement — un
      // appelant qui oublierait un check UI n'écrirait pas en base sans lui.
      // Throw (pas setError+return) : un composant qui vient d'appeler
      // createGroup lirait le state error d'AVANT ce render (stale closure)
      // s'il fallait le relire après le await plutôt que le catch ici même.
      // `groups` ne contient que ce que la politique RLS laisse voir, donc
      // uniquement les groupes UTILISABLES — les groupes créés pendant un essai
      // terminé n'y sont pas. C'est exactement le nombre que la règle du
      // Gratuit demande : un groupe d'essai ne consomme pas ce droit.
      if (!canCreateGroup(profile, groups.length)) {
        throw new Error(
          `${GROUP_PLAN_LIMIT_PREFIX}Le plan Gratuit donne droit à un groupe réutilisable ; au-delà, Solo est requis.`,
        );
      }

      setLoading(true);
      setError(null);

      try {
        const { data, error: supabaseError } = await supabase
          .from("date_poll_groups")
          .insert({
            creator_id: user.id,
            name: input.name,
            member_emails: input.member_emails,
          })
          .select()
          .single();

        if (supabaseError) {
          throw ErrorFactory.storage(
            `Failed to create group: ${supabaseError.message}`,
          );
        }

        logger.info(`Created group: ${data.name}`, "groups");

        // Recharger la liste des groupes
        await loadGroups();

        return normalizeGroup(data);
      } catch (err) {
        const error = err as Error;
        setError(error);
        logError(error, { component: "useGroups", operation: "createGroup" });
        return null;
      } finally {
        setLoading(false);
      }
    },
    // `profile` et `groups` entrent dans les dépendances : la garde les lit, et
    // une closure figée sur un ancien profil autoriserait une création que le
    // serveur refusera (ou l'inverse, juste après la fin d'un essai).
    [user, profile, groups, loadGroups],
  );

  // Mettre à jour un groupe existant
  const updateGroup = useCallback(
    async (
      id: string,
      input: UpdateGroupInput,
    ): Promise<DatePollGroup | null> => {
      if (!user) {
        throw ErrorFactory.auth("User must be authenticated to update groups");
      }

      setLoading(true);
      setError(null);

      try {
        const updateData: Partial<DatePollGroup> = {};
        if (input.name !== undefined) updateData.name = input.name;
        if (input.member_emails !== undefined)
          updateData.member_emails = input.member_emails;

        const { data, error: supabaseError } = await supabase
          .from("date_poll_groups")
          .update(updateData)
          .eq("id", id)
          .eq("creator_id", user.id)
          .select()
          .single();

        if (supabaseError) {
          throw ErrorFactory.storage(
            `Failed to update group: ${supabaseError.message}`,
          );
        }

        logger.info(`Updated group: ${data.name}`, "groups");

        // Recharger la liste des groupes
        await loadGroups();

        return normalizeGroup(data);
      } catch (err) {
        const error = err as Error;
        setError(error);
        logError(error, { component: "useGroups", operation: "updateGroup" });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user, loadGroups],
  );

  // Supprimer un groupe
  const deleteGroup = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) {
        throw ErrorFactory.auth("User must be authenticated to delete groups");
      }

      setLoading(true);
      setError(null);

      try {
        const { error: supabaseError } = await supabase
          .from("date_poll_groups")
          .delete()
          .eq("id", id)
          .eq("creator_id", user.id);

        if (supabaseError) {
          throw ErrorFactory.storage(
            `Failed to delete group: ${supabaseError.message}`,
          );
        }

        logger.info(`Deleted group: ${id}`, "groups");

        // Recharger la liste des groupes
        await loadGroups();

        return true;
      } catch (err) {
        const error = err as Error;
        setError(error);
        logError(error, { component: "useGroups", operation: "deleteGroup" });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user, loadGroups],
  );

  // Récupérer un groupe par son ID
  const getGroup = useCallback(
    (id: string): DatePollGroup | undefined => {
      return groups.find((g) => g.id === id);
    },
    [groups],
  );

  return {
    groups,
    lockedCount,
    canCreate: canCreateGroup(profile, groups.length),
    loading,
    error,
    loadGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    getGroup,
  };
}
